import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, BookOpen } from 'lucide-react';

const MurderAtTheMailboxSample = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-gradient-to-r from-orange-600 to-orange-800 py-16">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/books" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Books
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <BookOpen className="w-12 h-12 text-orange-200" />
            <div>
              <h1 className="text-4xl md:text-5xl font-serif font-bold text-white">
                Murder at the Mailbox
              </h1>
              <p className="text-orange-200 text-xl mt-2">Opening Chapters</p>
            </div>
          </div>
        </div>
      </section>

      {/* Chapter Content */}
      <article className="max-w-4xl mx-auto px-6 py-16">
        <div className="prose prose-lg max-w-none">
          <p className="text-slate-500 text-sm mb-8 italic text-center">Wednesday, October 17th</p>

          {/* Decorative Chapter Number */}
          <div className="text-center mb-12">
            <div className="inline-block">
              <div className="h-px w-16 bg-orange-300 mx-auto mb-4"></div>
              <h2 className="text-4xl font-serif font-bold text-slate-900">
                (1)
              </h2>
              <div className="h-px w-16 bg-orange-300 mx-auto mt-4"></div>
            </div>
          </div>

          {/* First paragraph with drop cap */}
          <p className="text-slate-700 leading-relaxed mb-6">
            <span className="float-left text-7xl font-serif font-bold text-orange-600 leading-none mr-2 mt-1">B</span>
            erkeley, California, is the city everyone loves to laugh at. I get that, really I do. For some inhabitants it's like every day is Flashback Friday. Tie-dyed shirts and tattoos never go out of style here. People's Park is populated by the offspring of those who sat in during the late '60s. There is no dearth of ire on ordinary civic matters or shortage of shade-grown fanatics who would recycle their chewing gum given their druthers. Every neighborhood is a Nuclear Free Zone, as if our enemies would take notice of the signs attached to telephone poles before annihilating the Left Coast.
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            The concept of Halloween in Berkeley, then, is nothing if not redundant. Each day is dress-up-and-appear-weird day in the City by the City by the Bay. Every season is open season on the unpredictable and often outrageous. Anything is possible in the land of patchouli oil and incense.
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            So seeing a woman sprawled against a mailbox on one of the toniest streets in the Berkeley Hills isn't something that would normally send antennae shooting out of my skull. In fact, the most enthusiasm I could muster was, "Zeppo honey, watch out for the homeless lady up ahead. Don't trip on her feet."
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            "Should we give her money or a Luna bar?" Zeppo asked, taking my one free hand while I locked the door to my purple Honda Fit with the other.
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            "Neither," I replied, pulling my coat closed against the nippy October night. "We're so close to College Avenue, she probably gets plenty of handouts."
          </p>

          {/* Pull Quote */}
          <div className="my-10 py-8 px-6 border-l-4 border-orange-400 bg-orange-50/50">
            <p className="text-xl md:text-2xl font-serif italic text-slate-800 leading-relaxed">
              "Despite the full moon and fog, I had no premonitions that this night would be anything but normal on the Berkeley scale."
            </p>
          </div>

          <p className="text-slate-700 leading-relaxed mb-6">
            I had parked a brisk J-walk across from the shingle pile inhabited by dear Elspeth Waldron, the most authentic lotus flower in the People's Republic of Berkeley. That she was 80-plus didn't keep her from decorating her house on par with all of the mansions on Russell Street to make it Berkeley's All Hallowed answer to Mardi Gras.
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            For the next few hours, while I attended a protest on the use of ambulance sirens in proximity to a Tibetan temple, Elspeth was going to haul out her Halloween decorations with Zeppo so that the other two men in my life, Zach and Andy, could bond alone at home over falafel and fifth-grade math. Despite the full moon and fog, I had no premonitions that this night would be anything but normal on the Berkeley scale.
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            "Mama, what's she saying?" Zeppo whispered as we approached the curb, the woman's one satin Chinese slipper caught in a perfect moonbeam. "And why isn't she wearing any underwear?"
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            The mailbox, against which I assumed the woman had conveniently found purchase, was tilted at a curious 45-degree angle, a convenient prop for a lunatic exposing herself. "And look at all that blood!" my five-year-old bellowed, pointing to the woman's mouth, which was painted theatrically with dark red lipstick.
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            It took seconds for my brain to catch up with my bulging eyes. I yanked Zeppo back onto the street with my body now blocking his view of the lady with the exposed private parts and a black China doll wig askew on her head.
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            "Run. Run to Elspeth and tell her there's a sick woman out here. Go. Now!" The panic in my voice and the shove I gave Zeppo obviously registered because I've never seen my rug rat run so fast in his little life. I waited a second to ensure Zeppo's safety before turning back to the rag doll who had already convinced me that she wasn't your run-of-the-mill Berkeley burnout in search of a cement crash pad.
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            "My … fan." The woman's whisper pulled me back. Her stare was vacant. Her skin tone bloodless.
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            "Your fan?" I squatted down to touch the cool, manicured hand on her lap partially covered by a cheap satin dress with dragon motif across the bodice. I lifted the lapels of the woman's Burberry trench coat but found nothing inside. No purse was beside her. Sensing I was searching for something, the woman wiggled one finger on a hand bent backward as if asking for a high five from behind. It was then that I noticed her thigh bone poking through the skin. Stifling a rising gag reflex, I reached for my phone to dial 911 but my hand shook so hard I dropped it onto her bare leg, which spasmed in response.
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            "Loud … " she croaked. The street was eerily still, no significant sign of life other than a feral cat darting over a stone wall.
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            "It's too loud?" I asked, wondering, medic that I am, if her eardrums were bleeding.
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            "Losh," she strained, her frustration palpable.
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            "Everything's okay," I replied, resting my hand on her bloodied forearm so she knew I was still with her. I began giving our whereabouts to Berkeley Police dispatch, who informed me that a unit was already on its way. I wanted to ask how that was possible since I'd just dialed but the woman's intact leg jerked to the sound of sirens in the distance.
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            "Sshh. Lotion," she tried again through a gurgle of blood.
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            "I don't see any lotion, or a fan," I said, gently tugging at her dress. Call me a lady. Say I tampered with evidence. But the poor woman's costume was hiked up past her hoo-hah over a hip that appeared to have popped right out of the socket.
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            "Do you live nearby? Is there anyone I can call?" I brushed chunks of tar from her face and sized up the situation. Pricey raincoat. Strands of blond hair peeking out from under a cheap wig. Expertly arched eyebrows and skin that had been polished to perfection. The cheap Chinese get-up wasn't going to throw me off the scent. A wealthy woman from one of the best neighborhoods in Berkeley was, for whatever reason, already dressed for Halloween and slowly slipping away against a mailbox that had been knocked senseless as well. I needed to get answers before she entered another astral plane all together.
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            "Who did this to you?" I pleaded, as the sirens grew louder and closer. I lay my own jacket over the woman for warmth. Her hands and feet began to curl inward. Her body was moving into a fetal position. I was losing her. "Was it a car?"
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            "Low … " was all the woman said before her eyes stared straight ahead, fixed and bleeding. Her leg jerked one last time, then she was dead. And I was shivering so hard I fell over, my big old butt landing on her broken, twisted hand.
          </p>

          {/* Decorative Chapter Divider */}
          <div className="text-center my-12">
            <div className="inline-block">
              <div className="h-px w-16 bg-orange-300 mx-auto mb-4"></div>
              <h2 className="text-4xl font-serif font-bold text-slate-900">
                (2)
              </h2>
              <div className="h-px w-16 bg-orange-300 mx-auto mt-4"></div>
            </div>
          </div>

          {/* Second chapter with drop cap */}
          <p className="text-slate-700 leading-relaxed mb-6">
            <span className="float-left text-7xl font-serif font-bold text-orange-600 leading-none mr-2 mt-1">M</span>
            y name is Clari Drake and for God's sake, if this ever happens to me pull down my nightie before dialing 911. I could see all the way to China, as Grandma Thorsen might have said, what with her knees all bent up like that. And that's the last thing I'd need in the last moment of my life: pubes on public display, which is silly of me to joke about but the shaking wasn't going to abate for another five years, and the nausea was creeping up and over me as if I'd eaten every piece of candy that Zach and Zeppo haul in every Halloween.
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            Allow me to back up. My formal name is Clarissa Thorsen Dyke, and this is your takeaway on me: I hail from Edina, Minnesota, and took my husband's name of Drake so that the fair people of San Francisco wouldn't make fun of my last name on TV where I was a hotter-than-hot and wholly heterosexual news reporter, ten years, fifty pounds, and one lifetime ago.
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            My career ended with a humiliating accident on camera involving a fencepost in the Sierra Nevada that put me into labor before being airlifted to safety in the governor's helicopter. The video of my ordeal was circulated to a national TV show that voted me biggest ass of the year. By then, I had the biggest ass of anyone, anywhere on television owing to the fact that I was pregnant. So, my career was shot to hell and I became a stay-at-home mom, eventually and inevitably itching for excitement.
          </p>

          {/* Pull Quote */}
          <div className="my-10 py-8 px-6 border-l-4 border-orange-400 bg-orange-50/50">
            <p className="text-xl md:text-2xl font-serif italic text-slate-800 leading-relaxed">
              "I have this innate need to stir up shit and right wrongs."
            </p>
          </div>

          <p className="text-slate-700 leading-relaxed mb-6">
            You see, I have this innate need to stir up shit and right wrongs, which last year resulted in my takedown of the power brokers at the posh private school my oldest boy attended. The place was all wrong for us, but that's beside the point. I got the headmaster, the head of the Board of Trustees, myself, and my son, Zachary, tossed out on our buttocks - them for fiscal malfeasance and me for digging in their dirt. Hey, I can't stand financial malfeasance, though I love saying those words. I felt I had no choice but to snoop around and take no prisoners. Problem is, I don't learn lessons well the first time, such as you don't mess with the big dogs, particularly when they are pedigreed and pampered Bull Terriers.
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            Still, as radical as this may sound, Berkeley is a great place to raise children, especially ours, so we never pondered a move to the burbs for a better school system. Zeppo has a weird name that no one notices because his friends are Izzy, Cassius, Obadiah, and Otto. I have yet to meet a Chico or a Harpo, but in the event I do, their mothers will surely understand that I liked the name Zeppo, and that's why I chose it for my second-born.
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            Zach was the one who started it all. I liked how big Zachary Michael Drake sounded. And I liked it even more when we discovered upon his birth that said child with big name would be a short person who would get lots of stares from strangers. Zach was born with achondroplasia, which in medical terms means he is a dwarf or little person. It also means he's the biggest person with the biggest heart that Andy and I know (next to his younger, but taller, brother, that is).
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            And according to Elspeth Waldron, the aforementioned octogenarian librarian with a Pilates card and with a Buddha bobblehead on her Prius dashboard, taking on the entire Bidwell-Coggin School meant not only that I'd found clarity, but that my self-esteem was rebooted and my third eye chakra realigned. And the best gig I could get was a job at the Berkeley Bi-Weekly, an unimpressive radical rag stuffed into boxes outside piercing boutiques and smoke shops near the university. The paper, widely ridiculed as the Berkeley Bi-Polar, is managed by a skeletal staff of losers of which I am now proudly one.
          </p>

          <p className="text-slate-700 leading-relaxed mb-6">
            The publisher had already announced she would give the BBW one more quarter of mouth-to-mouth before she shut down the bleeder, making this my best chance to break back into the real reporting business and get my groove on. And while it's a terrible thing to say, I didn't want to do a story on a trumped-up protest. I wanted a juicy story. And it's quite possible that a broken lady, a bent mailbox, and a fancy ZIP code were the perfect Halloween brew.
          </p>

          <p className="text-slate-700 leading-relaxed mb-6 font-semibold italic text-lg">
            But now, back to the sirens …
          </p>

          {/* Decorative end marker */}
          <p className="text-center text-3xl text-orange-300 my-12 font-serif">
            ❦
          </p>

          <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-8 mt-12 text-center">
            <p className="text-lg text-slate-700 mb-6">
              Want to keep reading? Get your copy of <span className="font-bold">Murder at the Mailbox</span> today!
            </p>
            <Link to="/books">
              <Button size="lg" className="bg-orange-600 hover:bg-orange-700 text-white px-8">
                View Book Details
              </Button>
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
};

export default MurderAtTheMailboxSample;
